import authService from './authService';
import { ensureOk, readData, readList, readPaginated, toQuery } from './apiResponse';
import type { PaginatedList } from '../types/activity';
import type {
  Document,
  DocumentFilter,
  DocumentFolder,
  DocumentFolderInput,
  DocumentInput,
  DocumentUrl,
  UploadRequest,
  UploadTicket,
} from '../types/document';

/** Nome da pasta de sistema onde ficam os arquivos do Acervo CAC (seed de primeiro login). */
export const CAC_FOLDER_NAME = 'Armas (CAC)';

/**
 * Normaliza a árvore de pastas: aceita tanto a lista aninhada (`children`) quanto uma lista
 * plana com `parent_folder_id`, e devolve sempre raízes com `children` ordenadas.
 */
export const buildFolderTree = (raw: DocumentFolder[]): DocumentFolder[] => {
  const flat: DocumentFolder[] = [];
  const visit = (list: DocumentFolder[]) => {
    for (const f of list) {
      flat.push({ ...f, children: undefined });
      if (Array.isArray(f.children) && f.children.length > 0) visit(f.children);
    }
  };
  visit(raw);
  const byId = new Map<number, DocumentFolder>();
  for (const f of flat) if (!byId.has(f.id)) byId.set(f.id, { ...f, children: [] });
  const roots: DocumentFolder[] = [];
  for (const f of byId.values()) {
    const parent = f.parent_folder_id ? byId.get(f.parent_folder_id) : undefined;
    if (parent && parent.id !== f.id) parent.children!.push(f);
    else roots.push(f);
  }
  const sort = (list: DocumentFolder[]) => {
    list.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, 'pt-BR'));
    list.forEach((f) => f.children && sort(f.children));
  };
  sort(roots);
  return roots;
};

/** Árvore → lista em profundidade, com o nível de cada pasta (para selects e acordeões). */
export const flattenFolders = (tree: DocumentFolder[], depth = 0): { folder: DocumentFolder; depth: number }[] =>
  tree.flatMap((f) => [{ folder: f, depth }, ...flattenFolders(f.children ?? [], depth + 1)]);

export const findFolderByName = (tree: DocumentFolder[], name: string): DocumentFolder | undefined =>
  flattenFolders(tree).find((x) => x.folder.name.localeCompare(name, 'pt-BR', { sensitivity: 'base' }) === 0)?.folder;

class DocumentService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  // ------------------------------------------------------------------ pastas

  /** GET /documents/folders — árvore (1 nível + subpastas) com `document_count`. */
  async getFolders(): Promise<DocumentFolder[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/documents/folders');
    return buildFolderTree(await readList<DocumentFolder>(response, 'Falha ao carregar as pastas'));
  }

  /** POST /documents/folders */
  async createFolder(input: DocumentFolderInput): Promise<DocumentFolder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/documents/folders', input);
    return readData<DocumentFolder>(response, 'Falha ao criar a pasta');
  }

  /** PUT /documents/folders/:id (pasta de sistema só renomeia) */
  async updateFolder(id: number, input: DocumentFolderInput): Promise<DocumentFolder> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/documents/folders/${id}`, input);
    return readData<DocumentFolder>(response, 'Falha ao atualizar a pasta');
  }

  /** DELETE /documents/folders/:id (409 se tiver documentos/subpastas ou for de sistema) */
  async deleteFolder(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/documents/folders/${id}`);
    await ensureOk(response, 'Falha ao excluir a pasta');
  }

  // ------------------------------------------------------------------ documentos

  /** GET /documents?folder_id=&q=&expiring=1&ref_type=&ref_id=&page=&page_size= */
  async list(filter: DocumentFilter = {}): Promise<PaginatedList<Document>> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/documents${toQuery({
        folder_id: filter.folder_id,
        q: filter.q,
        expiring: filter.expiring ? 1 : undefined,
        ref_type: filter.ref_type,
        ref_id: filter.ref_id,
        page: filter.page,
        page_size: filter.page_size,
      })}`,
    );
    return readPaginated<Document>(response, 'Falha ao listar os documentos');
  }

  /** GET /documents/:id */
  async getById(id: number): Promise<Document> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/documents/${id}`);
    return readData<Document>(response, 'Falha ao carregar o documento');
  }

  /** POST /documents/uploads — reserva o registro e devolve a SAS de escrita. */
  async createUpload(input: UploadRequest): Promise<UploadTicket> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/documents/uploads', input);
    return readData<UploadTicket>(response, 'Falha ao iniciar o envio do documento');
  }

  /** POST /documents/:id/complete { sha256, size_bytes } — confere o blob e marca `ready`. */
  async complete(id: number, sha256: string, sizeBytes: number): Promise<Document> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/documents/${id}/complete`, { sha256, size_bytes: sizeBytes });
    return readData<Document>(response, 'Falha ao confirmar o envio do documento');
  }

  /** PUT /documents/:id — metadados (não troca o arquivo). */
  async update(id: number, input: DocumentInput): Promise<Document> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/documents/${id}`, input);
    return readData<Document>(response, 'Falha ao atualizar o documento');
  }

  /** DELETE /documents/:id — exclusão lógica (o blob é apagado 7 dias depois). */
  async remove(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/documents/${id}`);
    await ensureOk(response, 'Falha ao excluir o documento');
  }

  /** GET /documents/:id/url?download=1 — SAS de leitura (inline ou attachment). */
  async getUrl(id: number, download = false): Promise<DocumentUrl> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/documents/${id}/url${toQuery({ download: download ? 1 : undefined })}`);
    return readData<DocumentUrl>(response, 'Falha ao gerar o link do documento');
  }
}

export const documentService = new DocumentService();
export default documentService;
